import { useCallback, useEffect, useState } from 'react';

import { AvailableFeatureFlags } from 'data/generated/graphql';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { useAllessehContentUpdateContext } from './AllessehContentUpdateContext';

export interface UseAllessehContentUpdateOptions {
  enabled?: boolean;
  initialContent?: AllessehContent;
}

/**
 * Returns the latest AllessehContent fetched for this originId after a subscription event,
 * or null if no update has been received yet. Only the calling component re-renders on update.
 * Pass enabled=false to skip tracking (e.g. for non-draft content).
 */
export const useAllessehContentUpdate = (
  originId: string,
  options?: UseAllessehContentUpdateOptions
): AllessehContent | null => {
  const { enabled = true, initialContent } = options ?? {};
  const { subscribe, unsubscribe, getLatestContent } = useAllessehContentUpdateContext();
  const autorefreshDraftArticlesEnabled = useFeatureFlag(AvailableFeatureFlags.AutorefreshDraftArticlesEnabled, true);
  const [updatedContent, setUpdatedContent] = useState<AllessehContent | null>(() =>
    enabled && autorefreshDraftArticlesEnabled ? getLatestContent(originId) : null
  );

  const handleUpdate = useCallback((content: AllessehContent) => {
    setUpdatedContent(content);
  }, []);

  useEffect(() => {
    if (!enabled || !autorefreshDraftArticlesEnabled) {
      setUpdatedContent(null);
      return () => {};
    }
    subscribe(originId, handleUpdate, initialContent);
    return () => unsubscribe(originId, handleUpdate);
    // initialContent is intentionally excluded: it seeds contentHashRef only on the first subscribe
    // call and must not trigger re-subscription when the caller re-renders with a new object reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, autorefreshDraftArticlesEnabled, originId, handleUpdate, subscribe, unsubscribe]);

  return updatedContent;
};
